import React, { useState } from 'react';
import {
  DiscoveredNpcDTO,
  featureToString,
  getCompassDirection,
  IItemInstanceDTO,
  PlaceDTO,
  MapDTO,
  terrainToString,
  TerrainType
} from '@hexcrawl/shared-utils';
import { useDiscoveryStore } from '../../stores/discoveryStore';
import { useGameStore } from '../../stores/gameStore';
import { useCheatStore } from '../../stores/cheatStore';
import { cheatButtonStyle } from '../../constants/cheatStyles';
import { MapScene } from '@/MapScene';

interface HexCoord { x: number; y: number }

function jumpToHex(x: number, y: number) {
  const mapScene = window.phaserGame?.scene?.getScene('MapScene') as MapScene | undefined;
  void mapScene?.jumpToHex(x, y);
}

function npcJumpHex(npc: DiscoveredNpcDTO): HexCoord | null {
  if (npc.discoveryState === 'known' || npc.x === undefined || npc.y === undefined) return null;
  return { x: npc.x, y: npc.y };
}

function itemJumpHex(item: IItemInstanceDTO): HexCoord | null {
  if (item.discoveryState !== 'located') return null;
  const loc = item.location;
  if (loc.type === 'location' && loc.id?.startsWith('hex:')) {
    const [x, y] = loc.id.slice('hex:'.length).split(',').map(Number);
    if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
  }
  return null;
}

function placeJumpHex(place: PlaceDTO): HexCoord | null {
  if (place.discoveryState === 'known') return null;
  // Only places pinned to a single hex are jumpable; regions/off-scene have no one hex.
  if (place.location.type === 'hex') return { x: place.location.x, y: place.location.y };
  return null;
}

// Lore rows are dense, so the shared cheat button is nudged a size smaller here.
const loreJumpButtonStyle: React.CSSProperties = {
  ...cheatButtonStyle,
  fontSize: 10,
  padding: '2px 8px',
};

function compassDirection(fromX: number, fromY: number, toX: number, toY: number): string {
  const dir = getCompassDirection(toX - fromX, toY - fromY);
  return dir ? `to the ${dir}` : 'nearby';
}

function hexDescription(map: MapDTO, x: number, y: number): string {
  const hex = map.hexes.find(h => h.x === x && h.y === y);
  if (!hex) return 'an unknown location';
  return featureToString(hex.feature) ?? terrainToString(hex.terrain);
}

function dominantTerrain(map: MapDTO, hexCoords: Array<{ x: number; y: number }>): string {
  const counts: Partial<Record<TerrainType, number>> = {};
  for (const { x, y } of hexCoords) {
    const hex = map.hexes.find(h => h.x === x && h.y === y);
    if (!hex) continue;
    counts[hex.terrain] = (counts[hex.terrain] ?? 0) + 1;
  }
  let best: TerrainType = TerrainType.PLAINS;
  let max = 0;
  for (const [t, n] of Object.entries(counts) as [TerrainType, number][]) {
    if (n > max) { max = n; best = t; }
  }
  return terrainToString(best);
}

function regionCentroid(hexCoords: Array<{ x: number; y: number }>): { x: number; y: number } {
  return {
    x: hexCoords.reduce((s, h) => s + h.x, 0) / hexCoords.length,
    y: hexCoords.reduce((s, h) => s + h.y, 0) / hexCoords.length,
  };
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatNpcLocation(npc: DiscoveredNpcDTO, map: MapDTO | null, playerX: number, playerY: number): string {
  if (npc.discoveryState === 'known' || npc.x === undefined || npc.y === undefined) return 'Whereabouts unknown';
  if (!map) return 'Location unknown';
  const desc = hexDescription(map, npc.x, npc.y);
  return `${capitalize(desc)} ${compassDirection(playerX, playerY, npc.x, npc.y)}`;
}

function formatQuestItemLocation(item: IItemInstanceDTO, map: MapDTO | null, playerX: number, playerY: number): string {
  // A 'known' item's whereabouts aren't revealed yet; 'located' ones are either in
  // the player's inventory (character location) or sitting on a known hex.
  if (item.discoveryState !== 'located') return 'Whereabouts unknown';
  const loc = item.location;
  if (loc.type === 'character') return 'In your possession';
  if (loc.type === 'location' && loc.id?.startsWith('hex:')) {
    const [x, y] = loc.id.slice('hex:'.length).split(',').map(Number);
    if (map && Number.isFinite(x) && Number.isFinite(y)) {
      return `${capitalize(hexDescription(map, x, y))} ${compassDirection(playerX, playerY, x, y)}`;
    }
  }
  return 'Location unknown';
}

function formatPlaceLocation(place: PlaceDTO, map: MapDTO | null, playerX: number, playerY: number): string {
  if (place.discoveryState === 'known') return 'Whereabouts unknown';
  const loc = place.location;
  switch (loc.type) {
    case 'off-scene': return 'Beyond this land';
    case 'hex': {
      if (!map) return 'Location unknown';
      const desc = hexDescription(map, loc.x, loc.y);
      return `${capitalize(desc)} ${compassDirection(playerX, playerY, loc.x, loc.y)}`;
    }
    case 'region': {
      if (!map) return 'Location unknown';
      const terrain = dominantTerrain(map, loc.hexes);
      const centroid = regionCentroid(loc.hexes);
      return `A region of ${terrain} ${compassDirection(playerX, playerY, centroid.x, centroid.y)}`;
    }
  }
}

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    fontSize: 10, fontWeight: 'bold', letterSpacing: 1.5, color: '#556',
    textTransform: 'uppercase', marginBottom: 8, borderBottom: '1px solid #223',
    paddingBottom: 4, fontFamily: 'monospace',
  }}>
    {children}
  </div>
);

const EntryRow: React.FC<{ name: string; location: string; pronouns?: string; description?: string; source?: string; onJump?: () => void }> = ({ name, location, pronouns, description, source, onJump }) => (
  <div style={{
    padding: '6px 8px',
    marginBottom: 6,
    background: 'rgba(22, 40, 60, 0.5)',
    border: '1px solid #2a3a5a',
    borderRadius: 6,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'monospace' }}>
      <span style={{ fontWeight: 'bold', fontSize: 13, color: '#d9c8a0' }}>
        {capitalize(name)}
      </span>
      {pronouns && (
        <span style={{ fontSize: 10, color: '#667' }}>({pronouns})</span>
      )}
      {source === 'creation' && (
        <span style={{ fontSize: 9, color: '#6a8a6a', fontFamily: 'monospace', letterSpacing: 0.5 }}>
          From your past
        </span>
      )}
      {onJump && (
        <>
          <span style={{ flex: 1 }} />
          <button onClick={onJump} title="Cheat: teleport here and trigger any encounter" style={loreJumpButtonStyle}>
            ⚡ Cheat: Jump
          </button>
        </>
      )}
    </div>
    {description && (
      <div style={{ fontSize: 11, color: '#8a9ab0', fontFamily: 'monospace', marginTop: 2, lineHeight: 1.4 }}>
        {description}
      </div>
    )}
    <div style={{ fontSize: 11, color: '#667', fontFamily: 'monospace', marginTop: 2 }}>
      {location}
    </div>
  </div>
);

const REPUTATION_COLORS: Record<string, string> = {
  enamoured: '#e8a0ff', devoted: '#e8a0ff', adoring: '#c87fff',
  grateful: '#80c8ff', friendly: '#80c8ff', warm: '#80c8ff',
  trusting: '#a0d8a0', fond: '#a0d8a0', respectful: '#a0d8a0',
  neutral: '#778899', curious: '#778899',
  wary: '#d4a843', envious: '#d4a843', suspicious: '#d4a843',
  distrustful: '#e07040', contemptuous: '#e07040', resentful: '#e07040',
  fearful: '#e04040', hostile: '#e04040', wrathful: '#e04040',
};

interface NpcEntryRowProps {
  name: string;
  location: string;
  pronouns?: string;
  reputation?: string;
  description?: string;
  expanded: boolean;
  onToggle: () => void;
  onJump?: () => void;
}

const NpcEntryRow: React.FC<NpcEntryRowProps> = ({ name, location, pronouns, reputation, description, expanded, onToggle, onJump }) => (
  <div style={{
    marginBottom: 6,
    background: 'rgba(22, 40, 60, 0.5)',
    border: '1px solid #2a3a5a',
    borderRadius: 6,
    overflow: 'hidden',
  }}>
    <div
      onClick={onToggle}
      style={{
        padding: '6px 8px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 6,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontFamily: 'monospace' }}>
          <span style={{ fontWeight: 'bold', fontSize: 13, color: '#d9c8a0' }}>
            {capitalize(name)}
          </span>
          {pronouns && (
            <span style={{ fontSize: 10, color: '#667' }}>({pronouns})</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <span style={{ fontSize: 11, color: '#667', fontFamily: 'monospace' }}>{location}</span>
          {reputation && (
            <span style={{
              fontSize: 9, fontFamily: 'monospace', letterSpacing: 0.5,
              color: REPUTATION_COLORS[reputation] ?? '#778899',
              textTransform: 'uppercase',
            }}>
              {reputation}
            </span>
          )}
        </div>
      </div>
      {onJump && (
        <button
          onClick={(e) => { e.stopPropagation(); onJump(); }}
          title="Cheat: teleport here and trigger any encounter"
          style={loreJumpButtonStyle}
        >
          ⚡ Cheat: Jump
        </button>
      )}
      <span style={{ fontSize: 10, color: '#556', flexShrink: 0, userSelect: 'none' }}>
        {expanded ? '▲' : '▼'}
      </span>
    </div>
    {expanded && description && (
      <div style={{
        padding: '0 8px 8px',
        fontSize: 11,
        color: '#8a9ab0',
        fontFamily: 'monospace',
        lineHeight: 1.5,
        borderTop: '1px solid #1a2a3a',
        paddingTop: 6,
        marginTop: 0,
      }}>
        {description}
      </div>
    )}
  </div>
);

interface Props {
  onClose: () => void;
  initialExpandedId?: string;
}

export const LoreModal: React.FC<Props> = ({ onClose, initialExpandedId }) => {
  const npcs = useDiscoveryStore((s) => s.npcs);
  const items = useDiscoveryStore((s) => s.items);
  const places = useDiscoveryStore((s) => s.places);
  const loading = useDiscoveryStore((s) => s.loading);
  const fetchDiscoveries = useDiscoveryStore((s) => s.fetchDiscoveries);
  const gameState = useGameStore((s) => s.gameState);
  const map = gameState?.map ?? null;
  const playerX = gameState?.location.x ?? 0;
  const playerY = gameState?.location.y ?? 0;
  const cheatsEnabled = useCheatStore((s) => s.cheatsEnabled);
  const [expandedNpcId, setExpandedNpcId] = useState<string | null>(initialExpandedId ?? null);

  const makeJumpHandler = (hex: HexCoord | null): (() => void) | undefined => {
    if (!cheatsEnabled || !hex) return undefined;
    return () => { jumpToHex(hex.x, hex.y); onClose(); };
  };

  // Always fetch fresh data when the modal opens
  React.useEffect(() => {
    if (gameState?.id) {
      void fetchDiscoveries(gameState.id);
    }
  }, [gameState?.id, fetchDiscoveries]);

  const isEmpty = npcs.length === 0 && items.length === 0 && places.length === 0;

  const needsDivider = (hasPrev: boolean, hasNext: boolean) => hasPrev && hasNext;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        zIndex: 4000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0e1f30',
          border: '2px solid #5aa0ff',
          borderRadius: 12,
          padding: '24px 28px',
          minWidth: 300,
          maxWidth: 480,
          width: '90vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 10,
            right: 12,
            background: 'none',
            border: 'none',
            color: '#667',
            fontSize: 18,
            cursor: 'pointer',
            lineHeight: 1,
            padding: 0,
          }}
        >
          ✕
        </button>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 20,
        }}>
          <img
            src="/assets/images/legendaries.png"
            alt=""
            style={{ width: 28, height: 28, imageRendering: 'pixelated' }}
          />
          <span style={{ fontSize: 16, fontWeight: 'bold', color: '#d9c8a0', fontFamily: 'monospace', letterSpacing: 1 }}>
            Lore
          </span>
        </div>

        {loading ? (
          <div style={{ color: '#666', fontSize: 13, fontFamily: 'monospace', padding: '16px 0' }}>
            Loading lore…
          </div>
        ) : isEmpty ? (
          <div style={{ color: '#556', fontSize: 13, fontFamily: 'monospace', padding: '16px 0', textAlign: 'center' }}>
            No lore discovered yet.
          </div>
        ) : (
          <div style={{ padding: '4px 0' }}>
            {npcs.length > 0 && (
              <>
                <SectionHeader>People</SectionHeader>
                {npcs.map((npc) => (
                  <NpcEntryRow
                    key={npc.id}
                    name={npc.name}
                    pronouns={npc.pronouns}
                    reputation={npc.reputation}
                    location={formatNpcLocation(npc, map, playerX, playerY)}
                    description={npc.playerExposedDescription}
                    expanded={expandedNpcId === npc.id}
                    onToggle={() => setExpandedNpcId(expandedNpcId === npc.id ? null : npc.id)}
                    onJump={makeJumpHandler(npcJumpHex(npc))}
                  />
                ))}
              </>
            )}
            {needsDivider(npcs.length > 0, items.length > 0) && (
              <div style={{ height: 1, background: '#223', margin: '12px 0 10px' }} />
            )}
            {items.length > 0 && (
              <>
                <SectionHeader>Items</SectionHeader>
                {items.map((item) => (
                  <EntryRow
                    key={item.id}
                    name={item.def.name}
                    location={formatQuestItemLocation(item, map, playerX, playerY)}
                    description={item.def.description}
                    onJump={makeJumpHandler(itemJumpHex(item))}
                  />
                ))}
              </>
            )}
            {needsDivider(npcs.length > 0 || items.length > 0, places.length > 0) && (
              <div style={{ height: 1, background: '#223', margin: '12px 0 10px' }} />
            )}
            {places.length > 0 && (
              <>
                <SectionHeader>Places</SectionHeader>
                {places.map((place) => (
                  <EntryRow
                    key={place.id}
                    name={place.name}
                    location={formatPlaceLocation(place, map, playerX, playerY)}
                    description={place.description}
                    source={place.source}
                    onJump={makeJumpHandler(placeJumpHex(place))}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
